import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LocationPreference } from '@demind-inc/core';
import { useTranslation } from 'react-i18next';

import {
  useAuthContext,
  usePreferences,
  useUpdateLocationPreference,
} from '../../../../../data-access';
import { AppSwitch, BasicDialog } from '../../../../common';
import './LocationSettingsSection.scss';

type LocationPreferences = Partial<LocationPreference>;
type LocationPreferencePatch = Partial<LocationPreference>;

const getBooleanValue = (value: unknown): boolean => {
  return typeof value === 'boolean' ? value : false;
};

const getStringValue = (value: unknown): string => {
  return typeof value === 'string' ? value : '';
};

const buildLocationPreferencePatch = (patch: LocationPreferencePatch): LocationPreferencePatch => {
  return patch;
};

const getAddressName = (pref: LocationPreferences, label: string): string => {
  const addr = pref.addresses?.[label];
  if (!addr || typeof addr !== 'object') return '';
  return getStringValue((addr as { addressName?: unknown }).addressName);
};

const LocationSettingsSection: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const {
    locationPreferences,
    isLoading: isLocationPreferencesLoading,
    isError: isLocationPreferencesError,
  } = usePreferences({ preferenceId: user.preferenceId ?? '' });
  const { updateLocationPreference, isPending } = useUpdateLocationPreference();
  const hasInitializedPreferencesRef = useRef(false);

  const normalizedLocationPreferences = useMemo(
    () => (locationPreferences ?? {}) as LocationPreferences,
    [locationPreferences]
  );

  const [isLocationEnabled, setIsLocationEnabled] = useState(false);
  const [homeAddress, setHomeAddress] = useState('');
  const [workAddress, setWorkAddress] = useState('');

  const [savedIsLocationEnabled, setSavedIsLocationEnabled] = useState(false);
  const [savedHomeAddress, setSavedHomeAddress] = useState('');
  const [savedWorkAddress, setSavedWorkAddress] = useState('');

  const [isRequestingLocationPermission, setIsRequestingLocationPermission] = useState(false);

  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');

  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [newAddressLabel, setNewAddressLabel] = useState('');
  const [newAddressName, setNewAddressName] = useState('');

  useEffect(() => {
    if (hasInitializedPreferencesRef.current || isLocationPreferencesLoading) {
      return;
    }

    const nextEnabled = getBooleanValue(normalizedLocationPreferences.enabled);
    const nextHome = getAddressName(normalizedLocationPreferences, 'home');
    const nextWork = getAddressName(normalizedLocationPreferences, 'work');

    setIsLocationEnabled(nextEnabled);
    setHomeAddress(nextHome);
    setWorkAddress(nextWork);

    setSavedIsLocationEnabled(nextEnabled);
    setSavedHomeAddress(nextHome);
    setSavedWorkAddress(nextWork);

    setSaveErrorMessage('');
    setSaveSuccessMessage('');
    hasInitializedPreferencesRef.current = true;
  }, [isLocationPreferencesLoading, normalizedLocationPreferences]);

  const hasToggleChanged = isLocationEnabled !== savedIsLocationEnabled;
  const hasHomeChanged = homeAddress.trim() !== savedHomeAddress.trim();
  const hasWorkChanged = workAddress.trim() !== savedWorkAddress.trim();

  const isBusy = isPending || isRequestingLocationPermission;
  const canEditAddresses = isLocationEnabled;

  const canSaveToggle = hasToggleChanged;
  const canSaveHome = hasHomeChanged;
  const canSaveWork = hasWorkChanged;
  const canSaveAll = hasToggleChanged || hasHomeChanged || hasWorkChanged;

  const clearStatusMessages = useCallback(() => {
    if (saveErrorMessage || saveSuccessMessage) {
      setSaveErrorMessage('');
      setSaveSuccessMessage('');
    }
  }, [saveErrorMessage, saveSuccessMessage]);

  useEffect(() => {
    clearStatusMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocationEnabled, homeAddress, workAddress]);

  const handleSaveHome = useCallback(async () => {
    if (!user.preferenceId) return;
    if (!canSaveHome) return;

    setSaveErrorMessage('');
    setSaveSuccessMessage('');

    try {
      const trimmed = homeAddress.trim();
      await updateLocationPreference({
        preferenceId: user.preferenceId,
        preferences: buildLocationPreferencePatch({
          addresses: {
            ...(normalizedLocationPreferences.addresses ?? {}),
            home: {
              ...(normalizedLocationPreferences.addresses?.home ?? { addressName: '' }),
              addressName: trimmed,
            },
          },
        }),
      });
      setSavedHomeAddress(trimmed);
      setSaveSuccessMessage(t('settings.location.homeAddressSaved'));
    } catch (error) {
      console.error('Failed to save home address:', error);
      setSaveErrorMessage(t('settings.location.homeAddressSaveError'));
    }
  }, [
    canSaveHome,
    homeAddress,
    normalizedLocationPreferences.addresses,
    t,
    updateLocationPreference,
    user.preferenceId,
  ]);

  const handleSaveWork = useCallback(async () => {
    if (!user.preferenceId) return;
    if (!canSaveWork) return;

    setSaveErrorMessage('');
    setSaveSuccessMessage('');

    try {
      const trimmed = workAddress.trim();
      await updateLocationPreference({
        preferenceId: user.preferenceId,
        preferences: buildLocationPreferencePatch({
          addresses: {
            ...(normalizedLocationPreferences.addresses ?? {}),
            work: {
              ...(normalizedLocationPreferences.addresses?.work ?? { addressName: '' }),
              addressName: trimmed,
            },
          },
        }),
      });
      setSavedWorkAddress(trimmed);
      setSaveSuccessMessage(t('settings.location.workAddressSaved'));
    } catch (error) {
      console.error('Failed to save work address:', error);
      setSaveErrorMessage(t('settings.location.workAddressSaveError'));
    }
  }, [
    canSaveWork,
    normalizedLocationPreferences.addresses,
    t,
    updateLocationPreference,
    user.preferenceId,
    workAddress,
  ]);

  const handleSaveAll = useCallback(async () => {
    if (!user.preferenceId) return;
    if (!canSaveAll) return;

    setSaveErrorMessage('');
    setSaveSuccessMessage('');

    try {
      const trimmedHome = homeAddress.trim();
      const trimmedWork = workAddress.trim();
      await updateLocationPreference({
        preferenceId: user.preferenceId,
        preferences: buildLocationPreferencePatch({
          ...(hasToggleChanged ? { enabled: isLocationEnabled } : {}),
          ...((hasHomeChanged || hasWorkChanged) && {
            addresses: {
              ...(normalizedLocationPreferences.addresses ?? {}),
              ...(hasHomeChanged
                ? {
                    home: {
                      ...(normalizedLocationPreferences.addresses?.home ?? { addressName: '' }),
                      addressName: trimmedHome,
                    },
                  }
                : {}),
              ...(hasWorkChanged
                ? {
                    work: {
                      ...(normalizedLocationPreferences.addresses?.work ?? { addressName: '' }),
                      addressName: trimmedWork,
                    },
                  }
                : {}),
            },
          }),
        }),
      });
      if (hasToggleChanged) setSavedIsLocationEnabled(isLocationEnabled);
      if (hasHomeChanged) setSavedHomeAddress(trimmedHome);
      if (hasWorkChanged) setSavedWorkAddress(trimmedWork);
      setSaveSuccessMessage(t('settings.location.settingsSaved'));
    } catch (error) {
      console.error('Failed to save location settings:', error);
      setSaveErrorMessage(t('settings.location.settingsSaveError'));
    }
  }, [
    canSaveAll,
    hasHomeChanged,
    hasToggleChanged,
    hasWorkChanged,
    homeAddress,
    isLocationEnabled,
    normalizedLocationPreferences.addresses,
    t,
    updateLocationPreference,
    user.preferenceId,
    workAddress,
  ]);

  const requestBrowserLocationPermission = useCallback(async () => {
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported in this browser.');
    }

    await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 60 * 1000,
      });
    });
  }, []);

  const addressRecord = useMemo(() => {
    return normalizedLocationPreferences.addresses ?? {};
  }, [normalizedLocationPreferences.addresses]);

  const sortedAddresses = useMemo(() => {
    const entries = Object.entries(addressRecord);
    const rank = (label: string) => {
      if (label === 'home') return 0;
      if (label === 'work') return 1;
      return 2;
    };
    return entries.sort(([a], [b]) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b);
    });
  }, [addressRecord]);

  const customAddresses = useMemo(() => {
    return sortedAddresses.filter(([label, pref]) => {
      if (label === 'home' || label === 'work') {
        return false;
      }
      const name = getStringValue((pref as { addressName?: unknown } | undefined)?.addressName);
      return Boolean(name.trim());
    });
  }, [sortedAddresses]);

  const openAddAddressModal = useCallback(() => {
    setSaveErrorMessage('');
    setSaveSuccessMessage('');
    setIsAddAddressOpen(true);
  }, []);

  const closeAddAddressModal = useCallback(() => {
    setIsAddAddressOpen(false);
    setNewAddressLabel('');
    setNewAddressName('');
  }, []);

  const handleDeleteAddress = useCallback(
    async (label: string) => {
      if (!user.preferenceId) return;
      if (!label) return;

      setSaveErrorMessage('');
      setSaveSuccessMessage('');

      try {
        const next: NonNullable<LocationPreference['addresses']> = { ...addressRecord };
        // Keep the label key, but clear its value
        next[label] = {
          ...(next[label] ?? { addressName: '' }),
          addressName: '',
          lat: undefined,
          lng: undefined,
          link: undefined,
        };

        await updateLocationPreference({
          preferenceId: user.preferenceId,
          preferences: buildLocationPreferencePatch({
            addresses: next,
          }),
        });

        if (label === 'home') {
          setHomeAddress('');
          setSavedHomeAddress('');
        }
        if (label === 'work') {
          setWorkAddress('');
          setSavedWorkAddress('');
        }
        setSaveSuccessMessage(t('settings.location.addressRemoved'));
      } catch (error) {
        console.error('Failed to delete address:', error);
        setSaveErrorMessage(t('settings.location.addressRemoveError'));
      }
    },
    [addressRecord, t, updateLocationPreference, user.preferenceId]
  );

  const handleAddAddress = useCallback(async () => {
    if (!user.preferenceId) return;

    const label = newAddressLabel.trim().toLowerCase();
    const name = newAddressName.trim();

    if (!label || !name) {
      setSaveErrorMessage(t('settings.location.labelAndAddressRequired'));
      return;
    }

    setSaveErrorMessage('');
    setSaveSuccessMessage('');

    try {
      const existing = addressRecord[label];
      await updateLocationPreference({
        preferenceId: user.preferenceId,
        preferences: buildLocationPreferencePatch({
          addresses: {
            ...addressRecord,
            [label]: {
              ...(existing ?? ({} as any)),
              addressName: name,
            },
          },
        }),
      });

      if (label === 'home') {
        setHomeAddress(name);
        setSavedHomeAddress(name);
      }
      if (label === 'work') {
        setWorkAddress(name);
        setSavedWorkAddress(name);
      }

      closeAddAddressModal();
      setSaveSuccessMessage(t('settings.location.addressSaved'));
    } catch (error) {
      console.error('Failed to add address:', error);
      setSaveErrorMessage(t('settings.location.addressSaveError'));
    }
  }, [
    addressRecord,
    closeAddAddressModal,
    newAddressLabel,
    newAddressName,
    t,
    updateLocationPreference,
    user.preferenceId,
  ]);

  const handleToggleChange = useCallback(
    async (checked: boolean) => {
      if (!user.preferenceId) {
        setIsLocationEnabled(checked);
        return;
      }

      // Turning OFF never needs permission.
      if (!checked) {
        setSaveErrorMessage('');
        setSaveSuccessMessage('');
        setIsRequestingLocationPermission(true);

        try {
          await updateLocationPreference({
            preferenceId: user.preferenceId,
            preferences: buildLocationPreferencePatch({ enabled: false }),
          });
          setIsLocationEnabled(false);
          setSavedIsLocationEnabled(false);
          setSaveSuccessMessage(t('settings.location.settingSaved'));
        } catch (error) {
          console.error('Failed to disable location:', error);
          // revert UI back to ON if we couldn't persist OFF
          setIsLocationEnabled(true);
          setSaveErrorMessage(t('settings.location.disableError'));
        } finally {
          setIsRequestingLocationPermission(false);
        }
        return;
      }

      // Turning ON should prompt for browser permission immediately.
      setSaveErrorMessage('');
      setSaveSuccessMessage('');
      setIsRequestingLocationPermission(true);

      try {
        await requestBrowserLocationPermission();
        await updateLocationPreference({
          preferenceId: user.preferenceId,
          preferences: buildLocationPreferencePatch({ enabled: true }),
        });
        setIsLocationEnabled(true);
        setSavedIsLocationEnabled(true);
        setSaveSuccessMessage(t('settings.location.settingSaved'));
      } catch (error) {
        console.warn('Location enable flow failed:', error);
        setIsLocationEnabled(false);
        setSaveErrorMessage(t('settings.location.enableError'));
      } finally {
        setIsRequestingLocationPermission(false);
      }
    },
    [requestBrowserLocationPermission, t, updateLocationPreference, user.preferenceId]
  );

  return (
    <div
      id="locationSettings"
      className="location-settings-section location-settings-section--new-tabs"
    >
      <div className="location-settings-section__block">
        <Stack gap={1} direction="row" alignItems="center">
          <Typography variant="subtitle1" className="location-settings-section__section-subtitle">
            {t('settings.location.description')}
          </Typography>
        </Stack>

        <Stack className="location-settings-section__card" gap={1.5}>
          <Stack
            className="location-settings-section__row"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Stack gap={0.25}>
              <Typography variant="body2" className="location-settings-section__row-title">
                {t('settings.location.enableLocation')}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={1}>
              <AppSwitch
                checked={isLocationEnabled}
                onChange={(event) => handleToggleChange(event.target.checked)}
              />
            </Stack>
          </Stack>
        </Stack>

        <Stack className="location-settings-section__card" gap={2}>
          <Typography variant="body2" className="location-settings-section__card-title">
            {t('settings.location.addresses')}
          </Typography>

          <Stack className="location-settings-section__addresses-grid">
            <Stack className="location-settings-section__address-item">
              <Stack direction="row" alignItems="flex-end" gap={1.25}>
                <TextField
                  size="small"
                  label={t('settings.location.home')}
                  placeholder={t('settings.location.homePlaceholder')}
                  value={homeAddress}
                  onChange={(event) => setHomeAddress(event.target.value)}
                  disabled={!canEditAddresses}
                  fullWidth
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSaveHome}
                  disabled={!user.preferenceId || isBusy || !canSaveHome}
                  className="location-settings-section__small-button location-settings-section__field-save"
                >
                  {t('settings.common.save')}
                </Button>
              </Stack>
            </Stack>

            <Stack className="location-settings-section__address-item">
              <Stack direction="row" alignItems="flex-end" gap={1.25}>
                <TextField
                  size="small"
                  label={t('settings.location.work')}
                  placeholder={t('settings.location.workPlaceholder')}
                  value={workAddress}
                  onChange={(event) => setWorkAddress(event.target.value)}
                  disabled={!canEditAddresses}
                  fullWidth
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSaveWork}
                  disabled={!user.preferenceId || isBusy || !canSaveWork}
                  className="location-settings-section__small-button location-settings-section__field-save"
                >
                  {t('settings.common.save')}
                </Button>
              </Stack>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="caption" className="location-settings-section__hint">
              {t('settings.location.addSavedLocationsHint')}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={openAddAddressModal}
              disabled={!user.preferenceId || isBusy || !canEditAddresses}
              className="location-settings-section__small-button"
            >
              {t('settings.location.addAddress')}
            </Button>
          </Stack>

          {customAddresses.length > 0 && (
            <Stack gap={1} className="location-settings-section__custom-list">
              {customAddresses.map(([label, pref]) => (
                <Stack
                  key={label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  className="location-settings-section__custom-list-item"
                >
                  <Stack gap={0.25}>
                    <Typography variant="body2" className="location-settings-section__custom-label">
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      className="location-settings-section__custom-value"
                    >
                      {getStringValue((pref as any)?.addressName)}
                    </Typography>
                  </Stack>

                  <IconButton
                    size="small"
                    onClick={() => handleDeleteAddress(label)}
                    disabled={!user.preferenceId || isBusy}
                    className="location-settings-section__delete-button"
                    aria-label={t('settings.location.removeAddressLabel', { label })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>

        {isLocationPreferencesLoading && (
          <Typography variant="caption" className="location-settings-section__status-text">
            {t('settings.location.loadingSavedSettings')}
          </Typography>
        )}
        {isLocationPreferencesError && (
          <Typography
            variant="caption"
            className="location-settings-section__status-text location-settings-section__status-text--error"
          >
            {t('settings.location.loadSavedSettingsError')}
          </Typography>
        )}
        {saveErrorMessage && (
          <Typography
            variant="caption"
            className="location-settings-section__status-text location-settings-section__status-text--error"
          >
            {saveErrorMessage}
          </Typography>
        )}
        {saveSuccessMessage && (
          <Typography
            variant="caption"
            className="location-settings-section__status-text location-settings-section__status-text--success"
          >
            {saveSuccessMessage}
          </Typography>
        )}

        <BasicDialog
          visible={isAddAddressOpen}
          title={t('settings.location.addAddressDialogTitle')}
          ctaString={t('settings.common.save')}
          cancelString={t('settings.common.cancel')}
          showCloseIcon
          onClose={closeAddAddressModal}
          onYes={handleAddAddress}
          isLoading={isBusy}
          disabled={!user.preferenceId || !newAddressLabel.trim() || !newAddressName.trim()}
          className="location-settings-section__add-address-dialog"
        >
          <Stack gap={2} sx={{ paddingTop: 1 }}>
            <TextField
              size="small"
              label={t('settings.location.label')}
              placeholder={t('settings.location.labelPlaceholder')}
              value={newAddressLabel}
              onChange={(e) => setNewAddressLabel(e.target.value)}
              fullWidth
            />

            <TextField
              size="small"
              label={t('settings.location.addressName')}
              placeholder={t('settings.location.addressNamePlaceholder')}
              value={newAddressName}
              onChange={(e) => setNewAddressName(e.target.value)}
              fullWidth
            />

            <Typography variant="caption" className="location-settings-section__hint">
              {t('settings.location.coordinatesLaterHint')}
            </Typography>
          </Stack>
        </BasicDialog>
      </div>
    </div>
  );
};

export default LocationSettingsSection;
